import { useCallback, useState } from 'react';

import { useTableData } from '@/shared/hooks/useTableData';
import { createComparator } from '@/shared/utils/sorting';
import {
  getAdminToolsListDisplayDescription,
  getAdminToolsListDisplayName,
} from '@/features/admin/lib/canonicalAdminToolLabels';

import type { ToolRowData } from '../ToolsTable/ToolsTable.types';
import type { ToolsTableV2Props, ToolsTableV2RowData } from './ToolsTableV2.types';
import { getToolSortConfig } from './toolSort';

export const TOOLS_PAGE_SIZE = 25;

type UseToolsTableV2Options = Pick<
  ToolsTableV2Props,
  'tools' | 'typeFilter' | 'sortId' | 'onSortChange' | 'showLastModified'
> & {
  searchQuery: string;
};

export function useToolsTableV2({
  tools,
  typeFilter,
  searchQuery,
  sortId,
  onSortChange,
  showLastModified,
}: UseToolsTableV2Options) {
  const query = searchQuery.trim().toLowerCase();
  const sortConfig = getToolSortConfig(showLastModified);
  const sort = sortConfig.resolve(sortId);

  // The search runs inside the transform so it can match `displayName` — the
  // label the row prints — rather than the raw API name behind it. The sort runs here too,
  // since the order is owned by the page rather than by `useTableData`.
  const transform = useCallback(
    (rows: ToolRowData[]): ToolsTableV2RowData[] => {
      type Row = ToolsTableV2RowData;
      const byName = (a: Row, b: Row) => a.displayName.localeCompare(b.displayName);
      const byDate = createComparator<Row>('updatedAt', sort.direction);
      const compare =
        sort.key === 'displayName'
          ? (a: Row, b: Row) => (sort.direction === 'asc' ? byName(a, b) : byName(b, a))
          : // Undated rows sort last either way, and the name tiebreak keeps them A to Z.
            (a: Row, b: Row) => byDate(a, b) || byName(a, b);

      return rows
        .map((tool) => ({
          ...tool,
          displayName: getAdminToolsListDisplayName(tool.id, tool.name),
          displayDescription: getAdminToolsListDisplayDescription(tool.id, tool.description),
        }))
        .filter((tool) => tool.displayName.toLowerCase().includes(query))
        .sort(compare);
    },
    [query, sort.key, sort.direction],
  );

  const tableData = useTableData<ToolRowData, ToolsTableV2RowData>({
    data: tools,
    transform,
    initialRowsPerPage: TOOLS_PAGE_SIZE,
  });

  const { handlePageChange } = tableData;

  // A filter, search or sort change replaces the list, so page 4 of the old one means nothing. The
  // reset is derived from the props during render rather than run from an effect: the state
  // being corrected belongs to this component, so React re-runs the render with the new page
  // before anything is committed, and the reader never sees the stale page paint first.
  const listShape = `${typeFilter}\u0000${query}\u0000${sortId ?? ''}`;
  const [listShapeOnScreen, setListShapeOnScreen] = useState(listShape);
  if (listShapeOnScreen !== listShape) {
    setListShapeOnScreen(listShape);
    handlePageChange(0);
  }

  const goToPage = useCallback(
    (page: number) => {
      handlePageChange(page);
    },
    [handlePageChange],
  );

  return {
    rows: tableData.paginatedData,
    totalCount: tableData.totalCount,
    page: tableData.page,
    rowsPerPage: tableData.rowsPerPage,
    sortBy: sort.key,
    sortDirection: sort.direction,
    /** Name is the only sortable column: A to Z first, then Z to A on a second use. */
    toggleNameSort: () => {
      if (sort.key === 'displayName' && sort.direction === 'asc') {
        onSortChange('displayName-desc');
      } else {
        onSortChange(sortConfig.toSortId('displayName-asc'));
      }
    },
    goToPage,
    showType: typeFilter === 'all',
    /** The reader has narrowed the list themselves, so an empty result is theirs to undo. */
    isNarrowed: typeFilter !== 'all' || query !== '',
  };
}
